import React, { useState } from 'react';

import Button from 'views/site/Button.js';
import Theme from 'styles/Theme.js';
import Utils from 'files/Utils.js';

/*
 * AffiliationBanner
 *
 * The affiliation disclosure, as a banner over the site rather than a card at
 * the bottom of the About page.
 *
 * WHERE IT SITS IS THE WHOLE REQUIREMENT. This is a disclosure some territories
 * require a visitor to be given, and a section most of the way down a page most
 * visitors never open does not give it to anybody. Over the site, on arrival,
 * on whichever page they landed on, is what the requirement actually asks for.
 *
 * It is dismissible, and dismissal is remembered per dealership. Every page of
 * a site carrying an immovable bar is worse for the customer and no better for
 * the dealership: a disclosure that cannot be put away gets scrolled past
 * rather than read, and there is nothing to show for it either way.
 *
 * The wording is approved. Do not edit it.
 */

const storageKey = id => `affiliation_disclaimer_dismissed_${id || 'unknown'}`;

// Blocked storage throws on access rather than returning null, and that is no
// reason to fail to draw the page.
const readDismissed = id => {
    try {
        return window.localStorage.getItem(storageKey(id)) === 'true';
    } catch(e) {
        return false;
    }
};

const AffiliationBanner = ({ dealership }) => {

    const [dismissed, setDismissed] = useState(() => readDismissed(dealership?.id));

    const mobile = Utils.isMobile();

    if(dealership?.show_affiliation_disclaimer !== true || dismissed) {
        return null;
    }

    const onDismiss = () => {
        setDismissed(true);
        try {
            window.localStorage.setItem(storageKey(dealership.id), 'true');
        } catch(e) {
            // The state still holds for this visit.
        }
    };

    return (
        <div
        aria-label={'About our company'}
        role={'region'}
        style={{
            bottom: 0,
            left: 0,
            padding: mobile ? 12 : 20,
            pointerEvents: 'none',
            position: 'fixed',
            right: 0,
            zIndex: 7000
        }}>
            <div style={{
                backgroundColor: Theme.colors.white,
                border: `1px solid ${Theme.colors.border()}`,
                borderRadius: 18,
                boxShadow: '0 18px 50px rgba(0,0,0,0.18)',
                margin: '0 auto',
                maxWidth: 820,
                padding: mobile ? '20px 20px 18px' : '24px 28px 22px',
                pointerEvents: 'all'
            }}>
                <span style={{
                    ...Theme.textStyles.subHeading({ fontSize: 16 }),
                    display: 'block',
                    marginBottom: 10
                }}>{'About our company'}</span>

                <span style={{
                    ...Theme.textStyles.body({ fontSize: 13, lineHeight: 1.65 }),
                    display: 'block',
                    marginBottom: mobile ? 16 : 18
                }}>{`${dealership.name} is a private company and is not affiliated with any government agency or municipality. We are not endorsed by any fire department or safety organization. One of our driving principles is educating families on home fire safety. We offer a range of high-quality early warning safety products for homeowners to purchase. We obtain our customers from referrals, surveys, social media, trade shows, door to door contact, and word of mouth. To request an educational evaluation for your home, contact us by clicking above or give us a call. We look forward to helping you.`}</span>

                <Button
                label={'I understand'}
                onClick={onDismiss}
                style={mobile ? { width: '100%' } : null}
                type={'primary'} />
            </div>
        </div>
    )
}

export default AffiliationBanner;
